import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  getDefaultPayrollWindow,
  MOCK_MY_JOBS,
  MOCK_USERS,
  type PayrollJob,
  type PayrollUserSummary,
  type PayrollWindow,
} from "./mockData";

type PayrollContextValue = {
  payrollWindow: PayrollWindow;
  setPayrollWindow: (window: PayrollWindow) => void;
  users: PayrollUserSummary[];
  approveUser: (userId: string) => void;
  myJobs: PayrollJob[];
  /** Whether the logged-in user has reviewed and approved their own statement. */
  myStatementApproved: boolean;
  approveMyStatement: () => void;
  /** Whether management has approved the logged-in user's payroll (unlocks PDF). */
  mgmtApprovedMyStatement: boolean;
};

const PayrollContext = createContext<PayrollContextValue | null>(null);

/** In-memory mock payroll store — no backend exists yet (PEAK-318). */
export function PayrollProvider({ children }: { children: ReactNode }) {
  const [payrollWindow, setPayrollWindow] = useState<PayrollWindow>(
    getDefaultPayrollWindow,
  );
  const [users, setUsers] = useState<PayrollUserSummary[]>(MOCK_USERS);
  const [myStatementApproved, setMyStatementApproved] = useState(false);

  const approveUser = useCallback((userId: string) => {
    setUsers((current) =>
      current.map((user) =>
        user.userId === userId ? { ...user, status: "approved" } : user,
      ),
    );
  }, []);

  const approveMyStatement = useCallback(() => {
    setMyStatementApproved(true);
  }, []);

  const value = useMemo(
    () => ({
      payrollWindow,
      setPayrollWindow,
      users,
      approveUser,
      myJobs: MOCK_MY_JOBS,
      myStatementApproved,
      approveMyStatement,
      mgmtApprovedMyStatement:
        users.find((user) => user.isCurrentUser)?.status === "approved",
    }),
    [
      payrollWindow,
      users,
      approveUser,
      myStatementApproved,
      approveMyStatement,
    ],
  );

  return (
    <PayrollContext.Provider value={value}>{children}</PayrollContext.Provider>
  );
}

export function usePayroll() {
  const context = useContext(PayrollContext);
  if (!context) {
    throw new Error("usePayroll must be used within PayrollProvider");
  }
  return context;
}
